/**
 * AdminCreditPacks — /admin/credit-packs
 *
 * Schedules seasonal % off on any of the three credit packs. Reads
 * `/api/admin/credit-packs` and PUTs to `/api/admin/credit-packs/{id}/discount`.
 * Setting percent_off=0 clears the discount so buyers see the original
 * price again. Start/end dates are optional; leaving them empty means
 * "starts now, no end date".
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Sparkles, RefreshCw, Save, X } from 'lucide-react';

// Compute the ISO string a <input type=datetime-local> should show
// given a stored server ISO string, in the user's local timezone.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (local) => (local ? new Date(local).toISOString() : null);

export default function AdminCreditPacks() {
  const { API } = useContext(AuthContext);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});   // { [pack_id]: {percent_off, starts_at, ends_at} }
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/credit-packs`);
      setPacks(data.packs || []);
      const d = {};
      for (const p of (data.packs || [])) {
        d[p.id] = {
          percent_off: p.discount?.percent_off || 0,
          starts_at: toLocalInput(p.discount?.starts_at),
          ends_at: toLocalInput(p.discount?.ends_at),
        };
      }
      setDrafts(d);
    } catch (_) {
      toast.error('Could not load credit packs');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (packId) => {
    const d = drafts[packId] || {};
    try {
      setSaving(packId);
      await axios.put(`${API}/admin/credit-packs/${packId}/discount`, {
        percent_off: parseInt(d.percent_off || 0, 10) || 0,
        starts_at: fromLocalInput(d.starts_at),
        ends_at: fromLocalInput(d.ends_at),
      });
      toast.success(d.percent_off > 0 ? 'Discount scheduled' : 'Discount cleared');
      load();
    } catch (_) {
      toast.error('Save failed');
    } finally { setSaving(null); }
  };

  const clear = async (packId) => {
    try {
      setSaving(packId);
      await axios.put(`${API}/admin/credit-packs/${packId}/discount`, {
        percent_off: 0, starts_at: null, ends_at: null,
      });
      toast.success('Discount cleared');
      load();
    } catch (_) {
      toast.error('Clear failed');
    } finally { setSaving(null); }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl" data-testid="admin-credit-packs-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-amber-500/15 text-amber-700 flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Billing</div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground">Credit-pack discounts</h1>
          <p className="text-sm text-muted-foreground">
            Schedule a seasonal % off. Members see a strike-through original price and pay the sale price on Stripe.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="admin-credit-packs-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading && <div className="text-center py-16 text-sm text-muted-foreground">Loading packs…</div>}

      <ul className="space-y-3">
        {packs.map((p) => {
          const d = drafts[p.id] || {};
          const salePrice = Math.max(1, Math.floor(p.price_usd * (100 - (parseInt(d.percent_off || 0, 10) || 0)) / 100));
          const onSale = (parseInt(d.percent_off || 0, 10) || 0) > 0;
          return (
            <li key={p.id} className={`rounded-2xl border p-4 ${p.discount_active ? 'border-rose-500/50 bg-rose-500/5' : 'border-border bg-card'}`} data-testid={`admin-credit-pack-${p.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-heading text-lg font-extrabold">{p.label}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                      {p.credits.toLocaleString()} credits
                    </span>
                    {p.discount_active && (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-rose-500 text-white rounded-full px-1.5 py-0.5">
                        Live sale
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Base price: <b>${p.price_usd}</b>
                    {onSale && (
                      <> · Preview sale price: <b className="text-rose-600">${salePrice}</b> ({d.percent_off}% off)</>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  Percent off
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={d.percent_off ?? 0}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [p.id]: { ...prev[p.id], percent_off: e.target.value } }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal"
                    data-testid={`admin-credit-pack-percent-${p.id}`}
                  />
                </label>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  Starts at (local)
                  <input
                    type="datetime-local"
                    value={d.starts_at || ''}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [p.id]: { ...prev[p.id], starts_at: e.target.value } }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal"
                    data-testid={`admin-credit-pack-starts-${p.id}`}
                  />
                </label>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  Ends at (local)
                  <input
                    type="datetime-local"
                    value={d.ends_at || ''}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [p.id]: { ...prev[p.id], ends_at: e.target.value } }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal"
                    data-testid={`admin-credit-pack-ends-${p.id}`}
                  />
                </label>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => save(p.id)}
                  disabled={saving === p.id}
                  className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid={`admin-credit-pack-save-${p.id}`}
                >
                  <Save className="w-3 h-3 mr-1" /> {saving === p.id ? 'Saving…' : 'Save discount'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => clear(p.id)}
                  disabled={saving === p.id}
                  className="rounded-full font-bold"
                  data-testid={`admin-credit-pack-clear-${p.id}`}
                >
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
